import { ContentService, CostOfCareState } from '@agingplan';
import { Library, MapLocation, MapRecords, MapState, US_MAPDATA, ZoomLocation, simplemaps_usmap_mapinfo } from '@agingplan/simplemap';
import { Component } from '@angular/core';
import { BehaviorSubject, OperatorFunction, filter, map } from 'rxjs';

@Component({
  selector: 'app-cost-of-care-map',
  templateUrl: './cost-of-care-map.component.html',
  styleUrls: ['./cost-of-care-map.component.scss'],
})
export class CostOfCareMapComponent {
  /**
   * The currently selected location. This is set after a user clicks on a state or city.
   */
  public location$ = new BehaviorSubject<CostOfCareLocation | undefined>(undefined);

  /**
   * Add the Cost of Care map data to the map data
   */
  public data$ = this.contentService.map_data$.pipe(
    // Ensure we have data to work with
    filter((records) => !!records && records.length > 0) as OperatorFunction<CostOfCareState[] | undefined, CostOfCareState[]>,
    // Add the Cost of Care data to the map data
    map((records) => {
      // By default we load the US map data
      const data = US_MAPDATA;
      const state_specific: MapRecords<MapState> = {};
      const locations: MapRecords<MapLocation> = {};

      // Loop through all of the map data and set the state and city names
      for (const record of records) {
        if (record.COC_CityName === '') {
          // If a city is not set, then we are dealing with a state
          const state = data.getOrCreateObjectFromKey(record.id, data.state_specific || {}, MapState);
          Object.assign(state_specific, { [record.id]: { ...state, ...record, name: record.COC_StateName, arrayId: record.id } });
        } else {
          // If a city is set, then we are dealing with a location
          const location = data.getOrCreateObjectFromKey(record.id, data.locations || {}, MapLocation);
          Object.assign(locations, { [record.id]: { ...location, ...record, name: record.COC_CityName, lat: record.latitude, lng: record.longitude, arrayId: record.id } });
        }
      }

      // Set the map data
      data.locations = locations;
      data.state_specific = state_specific;

      return data;
    }),
  );

  constructor(private contentService: ContentService) {}

  /**
   * The simplemap has been loaded and is ready to be interacted with.
   *
   * @param simplemap {Library} The simplemap library instance
   */
  public onReadyToLoad(simplemap: Library) {
    simplemap.mapinfo = simplemaps_usmap_mapinfo;
    simplemap.load();
  }
}

/**
 * A zoom location on the map detailing the Cost of Care state and city
 */
interface CostOfCareLocation extends ZoomLocation, Partial<CostOfCareState> {}
